import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { LOGO_URL } from "@/lib/api";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Public contract-signing page. Accessed via a unique token URL:
 *   /contracts/{token}
 * Never linked from the public site. Search-engine-noindex is applied via the same
 * meta tag mechanism as admin routes (see AdminNoIndex in App.js).
 *
 * Flow:
 *   1. Fetch contract by token → shows contract body + client field form
 *   2. Client fills in fields + types full name to sign
 *   3. POST /contracts/by-token/{token}/sign → thank-you screen
 */
export default function PublicContractSign() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [data, setData] = useState({
    full_name: "", phone: "", event_date: "", venue: "", city: "", package: "", notes: "",
  });
  const [signatureName, setSignatureName] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/contracts/by-token/${token}`)
      .then(r => {
        setContract(r.data);
        setData({ ...data, ...(r.data.client_data || {}) });
        if (r.data.client_data?.full_name) setSignatureName(r.data.client_data.full_name);
        if (r.data.signed_at) setDone(true);
      })
      .catch(() => setNotFound(true));
    // Apply noindex meta to this route (identical mechanism to admin routes)
    const m = document.createElement("meta");
    m.setAttribute("name", "robots"); m.setAttribute("content", "noindex, nofollow, noarchive, nosnippet");
    m.setAttribute("data-contract", "1");
    document.head.appendChild(m);
    return () => { document.querySelectorAll("meta[data-contract]").forEach(el => el.remove()); };
  }, [token]); // eslint-disable-line

  if (notFound) return <FullScreen>
    <div className="text-center max-w-md">
      <div className="font-serif text-3xl">Contract not found</div>
      <p className="text-white/60 mt-3">This link is invalid or has expired. Please contact Elite Pixels for a fresh link.</p>
    </div>
  </FullScreen>;

  if (!contract) return <FullScreen><Loader2 size={22} className="animate-spin text-[#D4AF37]"/></FullScreen>;

  if (done) return <FullScreen>
    <div className="text-center max-w-md" data-testid="contract-signed-thanks">
      <CheckCircle2 size={44} className="text-[#D4AF37] mx-auto"/>
      <div className="font-serif text-3xl mt-4">Thank you, {contract.signature_name || data.full_name}.</div>
      <p className="text-white/60 mt-3">Your signed contract is on record. We've emailed you a copy for your files, and we'll be in touch shortly to finalise your booking.</p>
    </div>
  </FullScreen>;

  const submit = async (e) => {
    e.preventDefault();
    if (!data.full_name.trim()) { toast.error("Please enter your full name"); return; }
    if (!signatureName.trim()) { toast.error("Please type your name as your signature"); return; }
    if (!acceptedTerms) { toast.error("Please accept the terms to sign"); return; }
    setSubmitting(true);
    try {
      await api.post(`/contracts/by-token/${token}/sign`, {
        client_data: data,
        signature_name: signatureName.trim(),
      });
      setDone(true);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not save signature. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="contract-page">
      {/* Header */}
      <div className="border-b border-white/10 py-6 mb-10">
        <div className="max-w-3xl mx-auto flex items-center gap-4 px-6">
          <img src={LOGO_URL} alt="Elite Pixels" className="h-10"/>
          <div>
            <div className="text-[0.65rem] tracking-[0.3em] uppercase text-[#D4AF37]">Contract</div>
            <div className="font-serif text-xl">Elite Pixels Photography &amp; Film</div>
          </div>
          <div className="ml-auto text-xs text-white/40 hidden sm:flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#D4AF37]"/> Secure signing link
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6">
        {/* Contract body (styled prose) */}
        <div className="bg-white text-black p-8 md:p-12 shadow-2xl" data-testid="contract-body"
             dangerouslySetInnerHTML={{ __html: contract.template_body_html }} />

        {/* Client fields + signature */}
        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="contract-form">
          <div>
            <div className="eyebrow mb-4">Your details</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name *" value={data.full_name} onChange={v => setData({ ...data, full_name: v })} testId="cf-full-name" />
              <Field label="Phone" value={data.phone} onChange={v => setData({ ...data, phone: v })} testId="cf-phone" />
              <Field label="Event Date" type="date" value={data.event_date} onChange={v => setData({ ...data, event_date: v })} testId="cf-event-date" />
              <Field label="Venue" value={data.venue} onChange={v => setData({ ...data, venue: v })} testId="cf-venue" />
              <Field label="City" value={data.city} onChange={v => setData({ ...data, city: v })} testId="cf-city" />
              <Field label="Package" value={data.package} onChange={v => setData({ ...data, package: v })} testId="cf-package" />
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Additional notes</span>
                  <textarea rows={3} value={data.notes} onChange={e => setData({ ...data, notes: e.target.value })}
                    className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
                    data-testid="cf-notes" />
                </label>
              </div>
            </div>
          </div>

          <div>
            <div className="eyebrow mb-4">Signature</div>
            <div className="bg-[#0a0a0a] border border-white/15 p-6 space-y-4">
              <label className="block">
                <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Type your full name to sign</span>
                <input value={signatureName} onChange={e => setSignatureName(e.target.value)}
                  placeholder="Your full legal name"
                  className="mt-1 w-full bg-black border border-white/15 px-3 py-3 font-serif text-2xl focus:border-[#D4AF37] outline-none"
                  data-testid="cf-signature" />
                {signatureName && (
                  <div className="mt-2 px-3 py-2 font-signature italic text-3xl text-[#D4AF37]" style={{ fontFamily: "'Great Vibes','Pinyon Script','Dancing Script',cursive" }}>
                    {signatureName}
                  </div>
                )}
              </label>
              <label className="flex items-start gap-3 text-sm">
                <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)}
                  className="mt-1 accent-[#D4AF37]" data-testid="cf-accept"/>
                <span className="text-white/75">I have read and agree to the terms of this contract. I understand my typed signature above serves as my legally binding signature.</span>
              </label>
            </div>
          </div>

          <button type="submit" disabled={submitting} className="btn-gold w-full justify-center py-4 text-sm"
                  data-testid="cf-submit">
            {submitting ? <><Loader2 size={14} className="animate-spin"/> Saving your signature…</> : "Sign contract"}
          </button>
          <p className="text-xs text-white/40 text-center">
            Once signed, a copy of this contract will be emailed to <strong className="text-white/70">{contract.lead_email || "your address"}</strong> and stored securely.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">{label}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
        data-testid={testId} />
    </label>
  );
}

function FullScreen({ children }) {
  return <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">{children}</div>;
}
